/**
 * FASE 24.2 — TRUST & TRANSPARENCY LAYER
 *
 * Kevia must clearly distinguish what it knows, infers, remembers,
 * assumes, or cannot confirm. Prevents false confidence.
 *
 * This module provides:
 *  - reliability scoring per context type
 *  - safe uncertainty phrasing
 *  - speculative suppression
 *  - prompt annotation for the LLM
 */

import { TRUST_LEVEL, trustQualifier } from "./pastoralToneGuide";

/**
 * Reliability score for each context component.
 * Higher = more trustworthy.
 */
const RELIABILITY = Object.freeze({
  supabase_data:        0.95,  // direct DB read, sanitized
  workflow_state:       0.90,  // persisted localStorage, validated
  entity_memory:        0.75,  // conversation summaries, may be stale
  runtime_timeline:     0.70,  // session events, may not cover full context
  inferred_from_signal: 0.55,  // signal scoring heuristics
  assumed_from_context: 0.40,  // contextual inference
  no_data:              0.00,
});

/**
 * @typedef {Object} TrustAnnotation
 * @property {string} source      — what generated this context
 * @property {number} reliability — 0-1
 * @property {string} level       — TRUST_LEVEL value
 * @property {string|null} qualifier — how to phrase claims from this source
 */

/**
 * Map a context source to its trust annotation.
 * @param {string} source
 * @returns {TrustAnnotation}
 */
export function getTrustAnnotation(source) {
  const reliability = RELIABILITY[source] ?? 0.5;
  let   level;

  if      (reliability >= 0.90) level = TRUST_LEVEL.CONFIRMED;
  else if (reliability >= 0.70) level = TRUST_LEVEL.REMEMBERED;
  else if (reliability >= 0.50) level = TRUST_LEVEL.INFERRED;
  else if (reliability >= 0.30) level = TRUST_LEVEL.ASSUMED;
  else                           level = TRUST_LEVEL.UNCERTAIN;

  return {
    source,
    reliability,
    level,
    qualifier: trustQualifier(level),
  };
}

/**
 * Build a trust context block for LLM prompt injection.
 * Tells Kevia what it should and should not assume.
 *
 * @param {{
 *   hasLiveData?: boolean,
 *   hasEntityMemory?: boolean,
 *   hasWorkflows?: boolean,
 *   hasRuntimeContext?: boolean,
 * }} sources
 * @returns {string|null}
 */
export function buildTrustPromptAnnotation({
  hasLiveData       = false,
  hasEntityMemory   = false,
  hasWorkflows      = false,
  hasRuntimeContext = false,
} = {}) {
  const available = [];

  if (hasLiveData)       available.push("datos en tiempo real de Kéves");
  if (hasEntityMemory)   available.push("memoria de conversaciones pasadas");
  if (hasWorkflows)      available.push("procesos pastorales activos");
  if (hasRuntimeContext) available.push("actividad reciente de sesión");

  // Only inject when there IS enriched context to tell the LLM about.
  // NEVER list what's "absent" — that causes the LLM to reply with
  // "No tengo acceso a..." which is false and breaks the pastoral experience.
  if (available.length === 0) return null;

  return `[contexto de confianza]\nInformación disponible en este mensaje: ${available.join(", ")}.`;
}

/**
 * Returns a pastoral uncertainty statement for a given claim.
 * @param {string} claim
 * @param {string} trustLevel — TRUST_LEVEL value
 * @returns {string}
 */
export function wrapWithUncertainty(claim, trustLevel) {
  const qualifier = trustQualifier(trustLevel);
  if (!qualifier) return claim;
  return `${qualifier} ${claim}`;
}

/**
 * Returns true if the claim appears speculative enough to suppress.
 * Used to prevent Kevia from surfacing low-confidence assertions.
 * @param {number} confidence — 0-1
 * @param {string} context    — "banner"|"chip"|"suggestion"|"prompt"
 */
export function shouldSuppressSpeculation(confidence, context = "prompt") {
  const THRESHOLDS = {
    banner:     0.70,
    chip:       0.60,
    suggestion: 0.50,
    prompt:     0.40,
  };
  return confidence < (THRESHOLDS[context] ?? 0.50);
}
